import { useState } from 'react';
import { Play } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Reveal } from '@/components/Reveal';
import { SpotlightGlow } from '@/components/SpotlightGlow';
import { useSpotlight } from '@/lib/useSpotlight';
import { SITE_IMAGES, SITE_VIDEOS } from '@/lib/siteImages';

// Full-bleed "signature reel" panel — a muted looping teaser that opens into
// a larger, sound-on video player when the visitor clicks play.
export const ShowreelSection = () => {
  const [open, setOpen] = useState(false);
  const spotlight = useSpotlight();

  return (
    <section className="relative bg-[#0A0A0A] overflow-hidden" data-testid="showreel-section">
      <div
        ref={spotlight.ref}
        onMouseMove={spotlight.onMouseMove}
        onMouseEnter={spotlight.onMouseEnter}
        onMouseLeave={spotlight.onMouseLeave}
        className="relative min-h-[56vh] flex items-center justify-center"
      >
        <div className="absolute inset-0">
          <video
            className="h-full w-full object-cover opacity-45"
            src={SITE_VIDEOS.showreel}
            poster={SITE_IMAGES.homeHero}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            data-testid="showreel-background-video"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/55" />
          <SpotlightGlow pos={spotlight.pos} active={spotlight.active} />
        </div>
        <Reveal className="relative z-10 flex flex-col items-center text-center px-4">
          <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-4">Our Signature Reel</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] max-w-2xl leading-[1.05]">
            WATCH THE STORY UNFOLD.
          </h2>
          <button
            onClick={() => setOpen(true)}
            data-testid="showreel-play-button"
            data-cursor-hover
            aria-label="Play showreel"
            className="mt-8 flex h-20 w-20 items-center justify-center rounded-full border-2 border-[hsl(var(--lxra-gold))] text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))] hover:text-[#0A0A0A] transition-colors duration-300 group"
          >
            <Play className="h-7 w-7 fill-current transition-transform duration-300 group-hover:scale-110" />
          </button>
        </Reveal>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl bg-black border-[hsl(var(--lxra-border))] p-0 overflow-hidden" data-testid="showreel-dialog">
          <DialogTitle className="sr-only">LXRA Studios Showreel</DialogTitle>
          <DialogDescription className="sr-only">Watch the LXRA Studios cinematic signature reel highlighting our photography and videography work.</DialogDescription>
          {open && (
            <video
              className="w-full h-auto max-h-[80vh]"
              src={SITE_VIDEOS.showreel}
              controls
              autoPlay
              playsInline
              data-testid="showreel-video-player"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
